import { MESApiService, MesfModal } from "@dexteel/mesf-core";
import {
	Autocomplete,
	Box,
	Divider,
	Grid2,
	MenuItem,
	TextField,
	Typography,
	createFilterOptions,
} from "@mui/material";
import { get } from "lodash-es";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import {
	type ProductType,
	getProductSizes,
} from "@/shared/repositories/ProductRepository";
import type {
	CampaignData,
	RecipeMeta,
	WorkOrder,
} from "../../models/CampaignData";
import { useStartCampaign } from "../../hooks/useStartCampaign";
import {
	getRecipesMeta,
	getWorkOrders,
} from "../../repositories/MillSetupRepository";

const filterWorkOrders = createFilterOptions<WorkOrder>({
	limit: 100,
	stringify: (wo) => `${wo.Name} ${wo.ERPProduct ?? ""}`,
});

const workOrderDetail = (wo: WorkOrder) => {
	const parts = [
		wo.ScheduledOrder != null ? `Schedule #${wo.ScheduledOrder}` : null,
		wo.ScheduledDateString?.slice(0, 10),
		wo.ERPProduct,
		wo.ScheduledBillets != null ? `${wo.ScheduledBillets} billets` : null,
		wo.ScheduledTons != null ? `${wo.ScheduledTons.toFixed(2)} tons` : null,
		wo.IsStarted ? "Started" : null,
	];
	return parts.filter(Boolean).join(" · ");
};

interface ChangeProductModalProps {
	open: boolean;
	campaign: CampaignData | null;
	onClose: () => void;
	onSuccess: () => void;
	onError: (message: string) => void;
}

interface FormValues {
	productTypeId: string;
	productSizeId: string;
	recipeId: string;
	scheduledBillets: number;
	scheduledWeight: number;
	workOrderName: string;
}

export const ChangeProductModal = ({
	open,
	campaign,
	onClose,
	onSuccess,
	onError,
}: ChangeProductModalProps) => {
	// Only render inner form when open — guarantees fresh mount each time
	if (!open) return null;
	return (
		<ChangeProductForm
			campaign={campaign}
			onClose={onClose}
			onSuccess={onSuccess}
			onError={onError}
		/>
	);
};

/** Inner form — mounts fresh each time modal opens, no stale state */
const ChangeProductForm = ({
	campaign,
	onClose,
	onSuccess,
	onError,
}: Omit<ChangeProductModalProps, "open">) => {
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Fetch ALL product types (no OnlyActives filter — legacy behavior)
	const { data: productTypes = [] } = useQuery({
		queryKey: ["productTypes", "all"],
		queryFn: async () => {
			const apiService = new MESApiService();
			const resp = await apiService.callV2("[MILL].[GetProductTypes]", []);
			if (resp.ok) {
				return get(resp, "data.tables[0].rows", []) as ProductType[];
			}
			throw new Error(resp.message || "Error fetching product types");
		},
	});
	const { data: allProductSizes = [] } = useQuery({
		queryKey: ["productSizes"],
		queryFn: () => getProductSizes(),
	});
	const { data: recipes = [] } = useQuery({
		queryKey: ["recipesMeta"],
		queryFn: getRecipesMeta,
	});
	// The running work order is pinned at mount so the 5s campaign refresh
	// cannot replace the operator's selection while the modal is open
	const runningWorkOrderName = useRef(campaign?.WorkOrderName ?? "").current;
	const campaignSizeId =
		campaign?.ProductSizeId != null ? String(campaign.ProductSizeId) : "";

	// Set when the operator changes the product, consumed once its work orders arrive
	const preselectPending = useRef(false);

	// Derive initial productTypeId from campaign's productSize
	const initialTypeId = useMemo(() => {
		if (!campaign?.ProductSizeId || allProductSizes.length === 0) return "";
		const size = allProductSizes.find((ps) => ps.Id === campaign.ProductSizeId);
		return size?.ProductTypeId != null ? String(size.ProductTypeId) : "";
	}, [campaign?.ProductSizeId, allProductSizes]);

	const { control, handleSubmit, watch, setValue } = useForm<FormValues>({
		defaultValues: {
			productTypeId: initialTypeId,
			productSizeId: campaignSizeId,
			recipeId: campaign?.RecipeId != null ? String(campaign.RecipeId) : "",
			scheduledBillets: campaign?.ScheduledBilletsCount ?? 0,
			scheduledWeight: campaign?.ScheduledWeight ?? 0,
			workOrderName: runningWorkOrderName,
		},
	});

	// Re-set productTypeId when initialTypeId resolves (if allProductSizes loaded after mount)
	useEffect(() => {
		if (initialTypeId) {
			setValue("productTypeId", initialTypeId);
		}
	}, [initialTypeId, setValue]);

	const selectedTypeId = watch("productTypeId");
	const selectedSizeId = watch("productSizeId");
	const watchedBillets = watch("scheduledBillets");
	const watchedWeight = watch("scheduledWeight");
	const watchedWorkOrderName = watch("workOrderName");

	const showScheduleError = watchedBillets < 1 && watchedWeight < 1;

	const { data: workOrders = [], isFetching: isFetchingWorkOrders } = useQuery({
		queryKey: ["workOrders", "byProductSize", selectedSizeId],
		queryFn: () => getWorkOrders({ productSizeId: Number(selectedSizeId) }),
		enabled: !!selectedSizeId,
	});

	// GetWorkOrders can leave the running work order out of its window
	const workOrderOptions = useMemo<WorkOrder[]>(() => {
		if (
			!runningWorkOrderName ||
			selectedSizeId !== campaignSizeId ||
			workOrders.some((wo) => String(wo.Name) === runningWorkOrderName)
		) {
			return workOrders;
		}
		return [
			{
				Id: -1,
				Name: runningWorkOrderName,
				ERPProduct: null,
				ScheduledTons: null,
				ScheduledBillets: null,
				ScheduledDateString: null,
				ScheduledOrder: null,
				IsStarted: true,
			},
			...workOrders,
		];
	}, [workOrders, runningWorkOrderName, selectedSizeId, campaignSizeId]);

	const fillSchedule = (wo: WorkOrder) => {
		setValue("scheduledBillets", wo.ScheduledBillets ?? 0);
		setValue(
			"scheduledWeight",
			wo.ScheduledTons != null ? Number(wo.ScheduledTons.toFixed(2)) : 0,
		);
	};

	// The mill runs the schedule in sequence, so the first not started one is next
	useEffect(() => {
		if (!preselectPending.current || isFetchingWorkOrders) return;
		preselectPending.current = false;
		const next = workOrders.find(
			(wo) => wo.ScheduledOrder != null && !wo.IsStarted,
		);
		setValue("workOrderName", next ? String(next.Name) : "");
		if (next) fillSchedule(next);
	}, [workOrders, isFetchingWorkOrders]);

	const selectedWorkOrder = useMemo(
		() =>
			workOrderOptions.find((wo) => String(wo.Name) === watchedWorkOrderName) ??
			null,
		[workOrderOptions, watchedWorkOrderName],
	);

	// Filter sizes by selected type
	const filteredProductSizes = useMemo(
		() =>
			selectedTypeId
				? allProductSizes.filter(
						(ps) => String(ps.ProductTypeId) === selectedTypeId,
					)
				: allProductSizes,
		[allProductSizes, selectedTypeId],
	);

	// Filter recipes by selected size
	const filteredRecipes = useMemo(
		() =>
			selectedSizeId
				? recipes.filter(
						(r: RecipeMeta) => String(r.ProductSizeId) === selectedSizeId,
					)
				: [],
		[recipes, selectedSizeId],
	);

	// Auto-select first recipe when size changes
	useEffect(() => {
		if (filteredRecipes.length > 0) {
			setValue("recipeId", String(filteredRecipes[0].Id));
		} else {
			setValue("recipeId", "");
		}
	}, [filteredRecipes, setValue]);

	const { startCampaign } = useStartCampaign({
		onSuccess: () => {
			setIsSubmitting(false);
			onSuccess();
		},
		onError: (err) => {
			setIsSubmitting(false);
			onError(err.message);
		},
	});

	const onSubmit = (data: FormValues) => {
		if (!data.productSizeId) return;
		if (data.scheduledBillets < 1 && data.scheduledWeight < 1) {
			onError("Scheduled billets or weight must be at least 1");
			return;
		}
		setIsSubmitting(true);
		startCampaign({
			productSizeId: Number(data.productSizeId),
			recipeId: data.recipeId ? Number(data.recipeId) : null,
			scheduledBillets: data.scheduledBillets,
			scheduledWeight: data.scheduledWeight,
			workOrderName: data.workOrderName || null,
		});
	};

	return (
		<MesfModal
			id="modal-change-product"
			title="Change product"
			open={true}
			handleClose={onClose}
			maxWidth="sm"
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
					{/* Section: Recipe */}
					<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
						Recipe
					</Typography>
					<Divider sx={{ mb: 1.5 }} />

					<Grid2 container spacing={2}>
						<Grid2 size={{ xs: 12 }}>
							<Controller
								name="productTypeId"
								control={control}
								render={({ field }) => (
									<TextField
										select
										label="Product Type"
										value={field.value}
										onChange={(e) => {
											field.onChange(e.target.value);
											setValue("productSizeId", "");
											preselectPending.current = true;
										}}
										fullWidth
										size="small"
									>
										<MenuItem value="">All</MenuItem>
										{productTypes.map((pt) => (
											<MenuItem key={pt.Id} value={String(pt.Id)}>
												{pt.Name}
											</MenuItem>
										))}
									</TextField>
								)}
							/>
						</Grid2>

						<Grid2 size={{ xs: 12 }}>
							<Controller
								name="productSizeId"
								control={control}
								rules={{ required: "Product size is required" }}
								render={({ field, fieldState: { error } }) => (
									<TextField
										select
										label="Product Size"
										value={field.value}
										onChange={(e) => {
											field.onChange(e.target.value);
											preselectPending.current = true;
										}}
										fullWidth
										size="small"
										error={!!error}
										helperText={error?.message}
									>
										<MenuItem value="" disabled>
											{filteredProductSizes.length === 0
												? "No products"
												: "- Select -"}
										</MenuItem>
										{filteredProductSizes.map((ps) => (
											<MenuItem key={ps.Id} value={String(ps.Id)}>
												{ps.Name}
											</MenuItem>
										))}
									</TextField>
								)}
							/>
						</Grid2>

						<Grid2 size={{ xs: 12 }}>
							<Controller
								name="recipeId"
								control={control}
								render={({ field }) => (
									<TextField
										select
										label="Recipe"
										value={field.value}
										onChange={(e) => field.onChange(e.target.value)}
										fullWidth
										size="small"
									>
										<MenuItem value="">No recipe</MenuItem>
										{filteredRecipes.map((r: RecipeMeta) => (
											<MenuItem key={r.Id} value={String(r.Id)}>
												{`${r.ProductSizeName} ${r.ProductTypeName} ${r.Name}`}
											</MenuItem>
										))}
									</TextField>
								)}
							/>
						</Grid2>
					</Grid2>

					{/* Section: Production Schedule */}
					<Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2 }}>
						Production Schedule
					</Typography>
					<Divider sx={{ mb: 1.5 }} />

					<Grid2 container spacing={2}>
						<Grid2 size={{ xs: 12 }}>
							<Controller
								name="workOrderName"
								control={control}
								rules={{ required: "Work order is required" }}
								render={({ field, fieldState: { error } }) => (
									<Autocomplete
										options={workOrderOptions}
										value={selectedWorkOrder}
										onChange={(_, wo) => {
											field.onChange(wo ? String(wo.Name) : "");
											if (wo) fillSchedule(wo);
										}}
										loading={isFetchingWorkOrders}
										noOptionsText={
											selectedSizeId
												? "No work orders for this product"
												: "Select a product size first"
										}
										getOptionLabel={(wo) => String(wo.Name)}
										isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
										filterOptions={filterWorkOrders}
										autoHighlight
										openOnFocus
										fullWidth
										size="small"
										renderOption={(props, wo) => (
											<li {...props} key={wo.Id}>
												<Box>
													<Typography sx={{ fontSize: 13, fontWeight: 600 }}>
														{wo.Name}
													</Typography>
													<Typography sx={{ fontSize: 11, color: "#888" }}>
														{workOrderDetail(wo)}
													</Typography>
												</Box>
											</li>
										)}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Work Order"
												error={!!error}
												helperText={error?.message}
											/>
										)}
									/>
								)}
							/>
						</Grid2>

						<Grid2 size={{ xs: 6 }}>
							<Controller
								name="scheduledBillets"
								control={control}
								render={({ field }) => (
									<TextField
										type="number"
										label="Billets Count"
										value={field.value}
										onChange={(e) => field.onChange(Number(e.target.value))}
										fullWidth
										size="small"
										error={showScheduleError}
									/>
								)}
							/>
						</Grid2>

						<Grid2 size={{ xs: 6 }}>
							<Controller
								name="scheduledWeight"
								control={control}
								render={({ field }) => (
									<TextField
										type="number"
										label="Tonnage"
										value={field.value}
										onChange={(e) => field.onChange(Number(e.target.value))}
										fullWidth
										size="small"
										error={showScheduleError}
									/>
								)}
							/>
						</Grid2>
					</Grid2>
				</MesfModal.Content>

				<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
					<FooterModalButtons
						isSubmitLoading={isSubmitting}
						onHide={() => onClose()}
						buttonLabel="Accept"
					/>
				</MesfModal.Actions>
			</form>
		</MesfModal>
	);
};
